/**
 * Internal SOP Library
 * ====================
 * Standard Operating Procedures for PHS internal operations.
 * 
 * Route: /wyldecrest/helpdesk/sops
 */

import React from 'react';
import GovernancePageLayout, { 
  DocumentSection, 
  SOPStep, 
  Checklist, 
  EscalationPath,
  WarningBox,
  InfoBox 
} from './GovernancePageLayout';
import { 
  BookOpen, 
  Phone, 
  FileText, 
  Clock, 
  CheckCircle, 
  Users,
  AlertTriangle,
  MessageSquare
} from 'lucide-react';

const sections = [
  { id: 'job-intake', title: 'Job Intake Process' },
  { id: 'triage-classification', title: 'Triage & Classification' },
  { id: 'contractor-dispatch', title: 'Contractor Dispatch' },
  { id: 'job-monitoring', title: 'Job Monitoring' },
  { id: 'completion-verification', title: 'Completion Verification' },
  { id: 'communication-standards', title: 'Communication Standards' },
];

export default function InternalSOPsPage() {
  return (
    <GovernancePageLayout
      title="Internal SOP Library"
      subtitle="Standard Operating Procedures for PHS Helpdesk Operations"
      icon={BookOpen}
      version="2.0"
      lastReviewed="2026-05-20"
      reviewedBy="Operations Manager"
      sections={sections}
    >
      {/* Job Intake Process */}
      <DocumentSection title="Job Intake Process" icon={Phone} id="job-intake">
        <p className="text-slate-300 mb-6">
          All maintenance requests must be processed through the standardized intake workflow
          to ensure proper classification, documentation, and routing.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Intake Procedure</h3>
        
        <SOPStep 
          number={1}
          title="Receive Request"
          description="Accept incoming maintenance request via phone, email, or portal submission."
          substeps={[
            'Log caller/submitter details in job notes',
            'Record timestamp of initial contact',
            'Capture property/asset reference'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Verify Property Details"
          description="Confirm property exists in the system and belongs to an active client."
          substeps={[
            'Search by postcode or property reference',
            'Verify client contract status is active',
            'Check for any property-specific notes or restrictions'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Create Job Record"
          description="Create a new FM job with all required information."
          substeps={[
            'Select appropriate job type (Reactive, Quoted Works, PPM)',
            'Enter detailed description of issue',
            'Attach any photos or documents provided',
            'Set initial priority based on triage matrix'
          ]}
        />

        <WarningBox title="Duplicate Check Required">
          Before creating a new job, always check for existing open jobs on the same property
          with similar descriptions. If a duplicate is found, add notes to the existing job
          rather than creating a new one.
        </WarningBox>

        <Checklist 
          title="Job Intake Checklist"
          items={[
            'Property verified in system',
            'Client contract status confirmed active',
            'Job type correctly selected',
            'Issue description is clear and detailed',
            'Priority set according to triage matrix',
            'Contact details recorded',
            'Any supporting documents attached'
          ]}
        />
      </DocumentSection>

      {/* Triage & Classification */}
      <DocumentSection title="Triage & Classification" icon={AlertTriangle} id="triage-classification">
        <p className="text-slate-300 mb-6">
          All jobs must be triaged within 30 minutes of creation using the priority matrix below.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Priority Matrix</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
            <h4 className="text-red-300 font-semibold mb-2">P1 - Emergency (2 hour response)</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Gas leak or smell of gas</li>
              <li>• Major water leak causing damage</li>
              <li>• Complete loss of heating in winter</li>
              <li>• Security breach / broken locks</li>
              <li>• Fire safety system failure</li>
            </ul>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">P2 - Urgent (24 hour response)</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Partial loss of heating/hot water</li>
              <li>• Electrical issues (not emergency)</li>
              <li>• Blocked drains causing inconvenience</li>
              <li>• Boiler malfunction</li>
              <li>• Roof leak (minor)</li>
            </ul>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">P3 - Standard (5 day response)</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• General repairs and maintenance</li>
              <li>• Non-urgent plumbing issues</li>
              <li>• Window/door repairs</li>
              <li>• Appliance repairs</li>
              <li>• Cosmetic damage</li>
            </ul>
          </div>
          
          <div className="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4">
            <h4 className="text-slate-300 font-semibold mb-2">P4 - Planned (Schedule as needed)</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Scheduled maintenance</li>
              <li>• Improvement works</li>
              <li>• Non-urgent inspections</li>
              <li>• Cosmetic improvements</li>
              <li>• PPM tasks</li>
            </ul>
          </div>
        </div>

        <InfoBox title="SLA Tracking">
          All job priorities have associated SLA targets. The system automatically flags jobs
          approaching or exceeding their SLA. Monitor the "At Risk" filter regularly.
        </InfoBox>
      </DocumentSection>

      {/* Contractor Dispatch */}
      <DocumentSection title="Contractor Dispatch" icon={Users} id="contractor-dispatch">
        <p className="text-slate-300 mb-6">
          Contractor assignment follows a structured process to ensure appropriate trade matching,
          coverage area compliance, and workload balancing.
        </p>

        <SOPStep 
          number={1}
          title="Identify Required Trade"
          description="Determine the trade type needed based on job classification."
          substeps={[
            'Review job description and attached evidence',
            'Select primary trade category',
            'Note any secondary trades that may be needed'
          ]}
        />

        <SOPStep 
          number={2}
          title="Filter Available Contractors"
          description="Use the contractor search to find suitable contractors."
          substeps={[
            'Filter by trade type',
            'Filter by coverage area (postcode)',
            'Check contractor availability and workload',
            'Review contractor performance scores'
          ]}
        />

        <SOPStep 
          number={3}
          title="Assign and Notify"
          description="Assign the job and trigger contractor notification."
          substeps={[
            'Select contractor from filtered list',
            'Add any special instructions to job notes',
            'Save assignment - system auto-sends notification',
            'Verify notification was delivered'
          ]}
        />

        <Checklist 
          title="Pre-Dispatch Verification"
          items={[
            'Trade type matches job requirements',
            'Contractor covers job location',
            'Contractor has capacity for timeline',
            'Performance score meets minimum threshold (>70%)',
            'No active disputes with this contractor',
            'Access instructions documented',
            'Contact details verified'
          ]}
        />
      </DocumentSection>

      {/* Job Monitoring */}
      <DocumentSection title="Job Monitoring" icon={Clock} id="job-monitoring">
        <p className="text-slate-300 mb-6">
          Active monitoring ensures jobs progress according to SLA and issues are identified early.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Daily Monitoring Tasks</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Time</th>
                <th className="pb-2">Task</th>
                <th className="pb-2">Action</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">08:00</td>
                <td>Review overnight jobs</td>
                <td>Triage any new submissions</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">10:00</td>
                <td>Check "At Risk" filter</td>
                <td>Follow up on approaching SLA breaches</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">14:00</td>
                <td>Midday pipeline review</td>
                <td>Verify scheduled jobs are on track</td>
              </tr>
              <tr>
                <td className="py-2">16:30</td>
                <td>End of day review</td>
                <td>Handover any urgent items for after-hours</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Escalation Triggers</h3>
        <EscalationPath levels={['Helpdesk Agent', 'Team Lead', 'Ops Manager', 'Director']} />
        
        <ul className="text-slate-300 space-y-2 ml-4">
          <li>• <strong className="text-white">P1 not acknowledged within 30 mins</strong> → Escalate to Team Lead</li>
          <li>• <strong className="text-white">P2 not scheduled within 4 hours</strong> → Escalate to Team Lead</li>
          <li>• <strong className="text-white">Any SLA breach</strong> → Escalate to Ops Manager</li>
          <li>• <strong className="text-white">Client complaint received</strong> → Escalate to Ops Manager</li>
          <li>• <strong className="text-white">Contractor dispute</strong> → Escalate to Director</li>
        </ul>
      </DocumentSection>

      {/* Completion Verification */}
      <DocumentSection title="Completion Verification" icon={CheckCircle} id="completion-verification">
        <p className="text-slate-300 mb-6">
          All jobs require verification before closure to ensure quality standards are met.
        </p>

        <SOPStep 
          number={1}
          title="Review Completion Report"
          description="Check the contractor's completion submission."
          substeps={[
            'Verify completion photos are clear and show work done',
            'Check completion notes are detailed',
            'Confirm any warranty information is documented',
            'Review time and materials if applicable'
          ]}
        />

        <SOPStep 
          number={2}
          title="Evidence Validation"
          description="Validate evidence meets governance standards."
          substeps={[
            'Before photos present and clear',
            'After photos show completed work',
            'Timestamps are correct',
            'Any certificates or compliance docs attached'
          ]}
        />

        <SOPStep 
          number={3}
          title="Close or Return"
          description="Approve completion or return for additional work."
          substeps={[
            'If satisfactory: Move to "Awaiting Invoice"',
            'If incomplete: Return with detailed feedback',
            'If quality issues: Escalate to Ops Manager'
          ]}
        />

        <WarningBox title="Evidence Requirements">
          Jobs cannot be closed without valid completion evidence. The system will
          block status transitions if evidence requirements are not met.
        </WarningBox>
      </DocumentSection>

      {/* Communication Standards */}
      <DocumentSection title="Communication Standards" icon={MessageSquare} id="communication-standards">
        <p className="text-slate-300 mb-6">
          All communications must be professional, timely, and documented in the job record.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Response Time Standards</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Channel</th>
                <th className="pb-2">Initial Response</th>
                <th className="pb-2">Follow-up</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Phone</td>
                <td>Immediate</td>
                <td>Call back within 2 hours if promised</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Email</td>
                <td>Within 4 hours</td>
                <td>Within 24 hours</td>
              </tr>
              <tr>
                <td className="py-2">Portal Message</td>
                <td>Within 2 hours</td>
                <td>Within 8 hours</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Checklist 
          title="Communication Checklist"
          items={[
            'All calls logged in job notes with summary',
            'Email correspondence saved to job record',
            'Commitments made are documented and tracked',
            'Client preferences for contact method noted',
            'Language is professional and clear',
            'Next steps are always communicated'
          ]}
        />
      </DocumentSection>
    </GovernancePageLayout>
  );
}
